'use client';

import React, { useState } from 'react';

type FormState = {
    name: string;
    email: string;
    resourceName: string;
    resourceUrl: string;
    category: string;
    description: string;
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

const CATEGORIES = [
    'Security & Privacy',
    'Web Standards',
    'Financial & Tax',
    'Search & Indexing',
    'Developer Tools',
    'Other',
];

//   Client-side validators  
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const URL_REGEX = /^https?:\/\/[^\s/$.?#].[^\s]*\.[a-z]{2,}/i;

function validateForm(form: FormState): FieldErrors {
    const errors: FieldErrors = {};

    if (!form.name.trim()) {
        errors.name = 'Name is required.';
    } else if (form.name.trim().length < 2) {
        errors.name = 'Name must be at least 2 characters.';
    } else if (form.name.trim().length > 100) {
        errors.name = 'Name must be under 100 characters.';
    }

    if (!form.email.trim()) {
        errors.email = 'Email address is required.';
    } else if (!EMAIL_REGEX.test(form.email.trim())) {
        errors.email = 'Please enter a valid email (e.g. you@domain.com).';
    }

    if (!form.resourceName.trim()) {
        errors.resourceName = 'Resource name is required.';
    } else if (form.resourceName.trim().length < 3) {
        errors.resourceName = 'Resource name must be at least 3 characters.';
    } else if (form.resourceName.trim().length > 200) {
        errors.resourceName = 'Resource name must be under 200 characters.';
    }

    if (!form.resourceUrl.trim()) {
        errors.resourceUrl = 'Resource URL is required.';
    } else if (!URL_REGEX.test(form.resourceUrl.trim())) {
        errors.resourceUrl = 'Enter a valid URL starting with https:// or http://.';
    }

    if (!form.category) {
        errors.category = 'Please select a category.';
    } else if (!CATEGORIES.includes(form.category)) {
        errors.category = 'Invalid category selected.';
    }

    if (!form.description.trim()) {
        errors.description = 'Description is required.';
    } else if (form.description.trim().length < 20) {
        errors.description = 'Please provide at least 20 characters of description.';
    } else if (form.description.trim().length > 1000) {
        errors.description = 'Description must be under 1000 characters.';
    }

    return errors;
}

//   Styles  
const getLabelStyle = (): React.CSSProperties => ({
    display: 'block',
    fontWeight: 700,
    fontSize: '0.875rem',
    marginBottom: '0.4rem',
    color: '#334155',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
});

const getInputStyle = (hasError: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '0.9rem 1.1rem',
    borderRadius: '12px',
    border: `1.5px solid ${hasError ? '#ef4444' : '#e2e8f0'}`,
    fontSize: '1rem',
    background: hasError ? '#fff5f5' : '#fff',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s, background 0.2s',
    fontFamily: 'inherit',
    color: '#0f172a',
    boxShadow: hasError ? '0 0 0 3px rgba(239,68,68,0.1)' : 'none',
});

const getErrorHintStyle = (): React.CSSProperties => ({
    marginTop: '0.35rem',
    fontSize: '0.82rem',
    color: '#dc2626',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
});

import { useLanguage } from '@/context/LanguageContext';

export default function ResourceSuggestionForm() {
    const { t } = useLanguage();
    const [form, setForm] = useState<FormState>({
        name: '',
        email: '',
        resourceName: '',
        resourceUrl: '',
        category: '',
        description: '',
    });
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
    const [submitStatus, setSubmitStatus] = useState<{
        loading: boolean;
        success: boolean;
        serverError: string;
    }>({ loading: false, success: false, serverError: '' });

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        const updated = { ...form, [name]: value };
        setForm(updated);

        // Re-validate that field in real-time once it's been touched
        if (touched[name as keyof FormState]) {
            const errs = validateForm(updated);
            setFieldErrors((prev) => ({ ...prev, [name]: errs[name as keyof FormState] }));
        }
        if (submitStatus.serverError) setSubmitStatus((s) => ({ ...s, serverError: '' }));
    };

    const handleBlur = (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name } = e.target;
        setTouched((prev) => ({ ...prev, [name]: true }));

        const errs = validateForm(form);
        setFieldErrors((prev) => ({ ...prev, [name]: errs[name as keyof FormState] }));

        // Reset blue focus ring
        e.target.style.boxShadow = fieldErrors[name as keyof FormState]
            ? '0 0 0 3px rgba(239,68,68,0.1)'
            : 'none';
        e.target.style.borderColor = fieldErrors[name as keyof FormState] ? '#ef4444' : '#e2e8f0';
    };

    const handleFocus = (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name } = e.target;
        if (!fieldErrors[name as keyof FormState]) {
            e.target.style.borderColor = 'var(--primary)';
            e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.12)';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Mark all fields as touched and run full validation
        const allTouched = Object.keys(form).reduce(
            (acc, k) => ({ ...acc, [k]: true }),
            {} as Record<keyof FormState, boolean>
        );
        setTouched(allTouched);

        const errs = validateForm(form);
        setFieldErrors(errs);

        if (Object.keys(errs).length > 0) {
            // Scroll to first error
            const firstErrField = document.querySelector('[data-error="true"]');
            firstErrField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        setSubmitStatus({ loading: true, success: false, serverError: '' });

        try {
            const res = await fetch('/api/suggest-resource', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            const data = await res.json();

            if (res.ok) {
                setSubmitStatus({ loading: false, success: true, serverError: '' });
                setForm({ name: '', email: '', resourceName: '', resourceUrl: '', category: '', description: '' });
                setFieldErrors({});
                setTouched({});
            } else {
                setSubmitStatus({ loading: false, success: false, serverError: data.error || 'Submission failed. Please try again.' });
            }
        } catch {
            setSubmitStatus({ loading: false, success: false, serverError: 'Network error. Please check your connection and try again.' });
        }
    };

    // Helper: error text for a field
    const err = (field: keyof FormState) =>
        touched[field] ? fieldErrors[field] : undefined;

    if (submitStatus.success) {
        return (
            <div style={{
                padding: '4rem',
                background: '#f0fdf4',
                borderRadius: '32px',
                textAlign: 'center',
                border: '1px solid #bbf7d0',
            }}>
                <div style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', marginBottom: '1rem' }}>🎉</div>
                <h3 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#065f46', marginBottom: '1rem' }}>
                    Suggestion Received!
                </h3>
                <p style={{ color: '#047857', fontSize: '1.1rem', marginBottom: '2rem', lineHeight: 1.7 }}>
                    Thank you for helping us grow the CorpToolset citation library.<br />
                    We'll review your submission within 2–3 business days.
                </p>
                <button
                    onClick={() => setSubmitStatus({ loading: false, success: false, serverError: '' })}
                    style={{
                        padding: '0.85rem 2.5rem',
                        borderRadius: '12px',
                        background: '#10b981',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '1rem',
                        cursor: 'pointer',
                        border: 'none',
                    }}
                >
                    Submit Another →
                </button>
            </div>
        );
    }

    return (
        <div style={{
            padding: '3rem',
            background: '#f8fafc',
            borderRadius: '32px',
            border: '1px solid #e2e8f0',
        }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                <h3 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '1rem' }}>Suggest a Resource</h3>
                <p style={{ color: '#64748b', maxWidth: '600px', margin: '0 auto', lineHeight: 1.7 }}>
                    Found an authoritative reference we should include? We continually expand our citation library
                    to serve the professional community.
                </p>
            </div>

            <form onSubmit={handleSubmit} noValidate style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
                {/* Name */}
                <div data-error={!!err('name')}>
                    <label htmlFor="srf-name" style={getLabelStyle()}>
                        Your Name <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                        id="srf-name"
                        name="name"
                        type="text"
                        placeholder={t('contact_name_placeholder')}
                        value={form.name}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        maxLength={100}
                        autoComplete="name"
                        style={getInputStyle(!!err('name'))}
                    />
                    {err('name') && (
                        <p style={getErrorHintStyle()}>⚠ {err('name')}</p>
                    )}
                </div>

                {/* Email */}
                <div data-error={!!err('email')}>
                    <label htmlFor="srf-email" style={getLabelStyle()}>
                        Email Address <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                        id="srf-email"
                        name="email"
                        type="email"
                        placeholder={t('contact_email_placeholder')}
                        value={form.email}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        autoComplete="email"
                        style={getInputStyle(!!err('email'))}
                    />
                    {err('email') && (
                        <p style={getErrorHintStyle()}>⚠ {err('email')}</p>
                    )}
                </div>

                {/* Resource Name */}
                <div data-error={!!err('resourceName')}>
                    <label htmlFor="srf-resourceName" style={getLabelStyle()}>
                        Resource Name <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                        id="srf-resourceName"
                        name="resourceName"
                        type="text"
                        placeholder="Name of the guide or documentation page"
                        value={form.resourceName}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        maxLength={200}
                        style={getInputStyle(!!err('resourceName'))}
                    />
                    {err('resourceName') && (
                        <p style={getErrorHintStyle()}>⚠ {err('resourceName')}</p>
                    )}
                </div>

                {/* Resource URL */}
                <div data-error={!!err('resourceUrl')}>
                    <label htmlFor="srf-resourceUrl" style={getLabelStyle()}>
                        Resource URL <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                        id="srf-resourceUrl"
                        name="resourceUrl"
                        type="url"
                        placeholder="https://example.com/resource"
                        value={form.resourceUrl}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        style={getInputStyle(!!err('resourceUrl'))}
                    />
                    {err('resourceUrl') && (
                        <p style={getErrorHintStyle()}>⚠ {err('resourceUrl')}</p>
                    )}
                </div>

                {/* Category */}
                <div style={{ gridColumn: '1 / -1' }} data-error={!!err('category')}>
                    <label htmlFor="srf-category" style={getLabelStyle()}>
                        Category <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <select
                        id="srf-category"
                        name="category"
                        value={form.category}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        style={{ ...getInputStyle(!!err('category')), appearance: 'auto' }}
                    >
                        <option value="">  Select a Category  </option>
                        {CATEGORIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                    {err('category') && (
                        <p style={getErrorHintStyle()}>⚠ {err('category')}</p>
                    )}
                </div>

                {/* Description */}
                <div style={{ gridColumn: '1 / -1' }} data-error={!!err('description')}>
                    <label htmlFor="srf-description" style={getLabelStyle()}>
                        Explain the technical relevance of this link:{' '}
                        <span style={{ color: '#94a3b8', textTransform: 'none', fontWeight: 400 }}>
                            (20–1000 characters)
                        </span>{' '}
                        <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <textarea
                        id="srf-description"
                        name="description"
                        rows={4}
                        placeholder="Provide a brief summary of what this document covers and why it belongs in our citation registry..."
                        value={form.description}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        maxLength={1000}
                        style={{ ...getInputStyle(!!err('description')), resize: 'vertical', minHeight: '110px' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.3rem' }}>
                        {err('description')
                            ? <p style={{ ...getErrorHintStyle(), margin: 0 }}>⚠ {err('description')}</p>
                            : <span />
                        }
                        <span style={{
                            fontSize: '0.8rem',
                            color: form.description.length > 950 ? '#ef4444' : '#94a3b8',
                            fontWeight: form.description.length > 950 ? 700 : 400,
                        }}>
                            {form.description.length}/1000
                        </span>
                    </div>
                </div>

                {/* Server Error Banner */}
                {submitStatus.serverError && (
                    <div style={{
                        gridColumn: '1 / -1',
                        padding: '1rem 1.25rem',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '12px',
                        color: '#dc2626',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}>
                        ⚠️ {submitStatus.serverError}
                    </div>
                )}

                {/* Submit */}
                <div style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                    <button
                        type="submit"
                        disabled={submitStatus.loading}
                        className="btn-primary"
                        style={{
                            padding: '1rem 3.5rem',
                            fontSize: '1.1rem',
                            opacity: submitStatus.loading ? 0.7 : 1,
                            cursor: submitStatus.loading ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {submitStatus.loading ? '⏳ Submitting...' : 'Submit a Reference →'}
                    </button>
                </div>
            </form>
        </div>
    );
}
